import { useEffect, useState } from "react";
import { Plus, Trash2, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, PERMS } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/Auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

export function IpList() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [v, setV] = useState("");
  useEffect(() => { api.get("/settings").then((r) => setList(r.data.ip_prefixes || [])); }, []);
  const save = async (next) => {
    try {
      const r = await api.put("/ip-prefixes", { ip_prefixes: next });
      setList(r.data.ip_prefixes);
      setV("");
      toast.success("Daftar IP disimpan");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const mine = user?.ip && /^\d+\.\d+\.\d+\.\d+$/.test(user.ip) ? user.ip.split(".").slice(0, 3).join(".") + "." : user?.ip;
  const covered = list.some((p) => user?.ip?.startsWith(p));
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
      <h2 className="font-display text-2xl font-bold flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-700" />IP kantor yang diizinkan</h2>
      <p className="text-xs text-slate-500">Awalan IP publik, mis. <code>36.68.54.</code>. Jika daftar kosong, staf bisa masuk dari mana saja. IP Anda sekarang: <code className="text-emerald-600">{user?.ip}</code></p>
      <div className="flex gap-2">
        <Input data-testid="ip-prefix-input" placeholder="36.68.54." value={v} onChange={(e) => setV(e.target.value)} className="bg-white font-mono" />
        <Button data-testid="ip-prefix-add" disabled={!v.trim()} onClick={() => save([...list, v.trim()])}><Plus className="h-4 w-4" /></Button>
      </div>
      {mine && !covered && (
        <Button data-testid="ip-add-mine" variant="outline" size="sm" onClick={() => save([...list, mine])}>
          <Plus className="h-4 w-4 mr-1" />Izinkan IP saya sekarang ({mine})
        </Button>
      )}
      <div className="flex flex-wrap gap-2">
        {list.map((p) => (
          <span key={p} data-testid={`ip-prefix-${p}`} className="inline-flex items-center gap-1 rounded-full bg-slate-100 pl-3 pr-1 py-1 font-mono text-sm">
            {p}<button data-testid={`ip-prefix-remove-${p}`} onClick={() => save(list.filter((x) => x !== p))} className="p-1 hover:text-red-600"><X className="h-3 w-3" /></button>
          </span>
        ))}
        {!list.length && <span className="text-xs text-amber-700">Belum ada pembatasan IP.</span>}
      </div>
    </section>
  );
}

const DEFAULT_PERMS = ["inbox", "slots", "check_balance", "switch_sim", "send_sms", "send_ussd", "activity"];

function PermPicker({ value, onChange, id, disabled }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div data-testid={`perm-picker-${id}`} className="grid grid-cols-2 md:grid-cols-5 gap-x-4 gap-y-2">
      {PERMS.map(([k, label]) => (
        <label key={k} className={`flex items-center gap-2 text-xs ${disabled ? "text-slate-400" : "text-slate-700 cursor-pointer"}`}>
          <Checkbox data-testid={`perm-${id}-${k}`} checked={value.includes(k)} disabled={disabled} onCheckedChange={() => toggle(k)} />{label}
        </label>
      ))}
    </div>
  );
}

function NewUser({ onDone }) {
  const [f, setF] = useState({ username: "", name: "", password: "" });
  const [perms, setPerms] = useState(DEFAULT_PERMS);
  const save = async () => {
    try {
      await api.post("/users", { ...f, role: "staff", perms });
      toast.success("Akun staf dibuat");
      setF({ username: "", name: "", password: "" });
      setPerms(DEFAULT_PERMS);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="grid md:grid-cols-4 gap-2">
      <Input data-testid="new-user-username" placeholder="username" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} className="bg-white" />
      <Input data-testid="new-user-name" placeholder="Nama tampilan" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-white" />
      <Input data-testid="new-user-password" type="password" placeholder="Kata sandi (min 6)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="bg-white" />
      <Button data-testid="new-user-submit" onClick={save} className="bg-emerald-500 hover:bg-emerald-600 text-white"><Plus className="h-4 w-4 mr-1" />Buat akun staf</Button>
      <div className="md:col-span-4 rounded border border-slate-200 bg-slate-50 p-3 space-y-2">
        <p className="text-xs uppercase tracking-wider text-slate-500">Akses yang boleh dipakai (yang tidak dicentang tidak bisa dibuka)</p>
        <PermPicker id="new" value={perms} onChange={setPerms} />
      </div>
    </div>
  );
}

function UserRow({ u, me, onChange }) {
  const upd = async (patch) => {
    try {
      await api.put(`/users/${u.id}`, patch);
      onChange();
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  const resetPw = () => {
    const p = window.prompt(`Kata sandi baru untuk ${u.username} (min 6 karakter)`);
    if (p) upd({ password: p }).then((ok) => ok && toast.success("Kata sandi diganti"));
  };
  const del = async () => {
    if (!window.confirm(`Hapus akun ${u.username}?`)) return;
    await api.delete(`/users/${u.id}`);
    onChange();
  };
  const isMe = u.id === me;
  return (
    <div data-testid={`user-row-${u.username}`} className="flex flex-wrap items-center gap-x-6 gap-y-2 p-4">
      <div className="flex-1 min-w-[160px]">
        <div className="font-semibold">{u.name} {u.role === "admin" && <span className="ml-1 text-[10px] uppercase rounded bg-emerald-500 text-white px-1.5 py-0.5">admin</span>}</div>
        <div className="text-xs font-mono text-slate-500">{u.username}</div>
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-500">Aktif
        <Switch data-testid={`user-active-${u.username}`} checked={u.active} disabled={isMe} onCheckedChange={(v) => upd({ active: v })} />
      </label>
      <label className="flex items-center gap-2 text-xs text-slate-500">Bebas IP
        <Switch data-testid={`user-ipexempt-${u.username}`} checked={u.role === "admin" || u.ip_exempt} disabled={u.role === "admin"} onCheckedChange={(v) => upd({ ip_exempt: v })} />
      </label>
      <Button data-testid={`user-resetpw-${u.username}`} size="sm" variant="outline" onClick={resetPw}>Ganti sandi</Button>
      {!isMe && <Button data-testid={`user-delete-${u.username}`} size="sm" variant="ghost" className="text-red-600" onClick={del}><Trash2 className="h-4 w-4" /></Button>}
      <div className="basis-full">
        {u.role === "admin"
          ? <p className="text-xs text-slate-500">Admin master: semua akses.</p>
          : <PermPicker id={u.username} value={u.perms || []} onChange={(perms) => upd({ perms }).then((ok) => ok && toast.success("Akses disimpan"))} />}
      </div>
    </div>
  );
}

export default function Users() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const load = () => api.get("/users").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="max-w-4xl space-y-5">
      <div>
        <p className="text-xs uppercase tracking-wider text-slate-500">Admin</p>
        <h1 className="font-display text-4xl font-bold">Akun & akses IP</h1>
      </div>
      <IpList />
      <section className="rounded-lg border border-slate-200 bg-white">
        <div className="p-4 border-b border-slate-200 space-y-3">
          <h2 className="font-display text-2xl font-bold">Akun</h2>
          <NewUser onDone={load} />
        </div>
        <div className="divide-y divide-slate-200">{list.map((u) => <UserRow key={u.id} u={u} me={user?.id} onChange={load} />)}</div>
      </section>
    </div>
  );
}
